// GstReports.js — Monthly GST reporting dashboard.
// Single fetch from /api/reports/gst returns Hotel invoices, Restaurant
// invoices, HSN-wise, Tax-slab-wise and Daily totals. The page surfaces
// five tabs and dedicated GSTR-1 B2B / B2C views.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Container, Card, CardContent, Typography, TextField, Button,
  Grid, Tabs, Tab, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Chip, Snackbar, Alert, CircularProgress, MenuItem,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as XLSX from 'xlsx';
import config from '../../config';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const currency = company?.currency ?? '₹';

const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtMoney = (n) =>
  Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
const fmtDate = (v) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('en-IN');
};

const KpiCard = ({ title, value, sub, color = '#1976d2' }) => (
  <Card sx={{ height: '100%', borderLeft: `4px solid ${color}` }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant='h6' fontWeight='700' sx={{ mt: 0.5, color }}>{value}</Typography>
      {sub && <Typography variant='caption' color='text.secondary'>{sub}</Typography>}
    </CardContent>
  </Card>
);

const SalesTable = ({ rows, showCustomer = true }) => (
  <TableContainer component={Card}>
    <Table size='small'>
      <TableHead>
        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
          <TableCell>Invoice #</TableCell>
          <TableCell>Date</TableCell>
          {showCustomer && <TableCell>Customer</TableCell>}
          {showCustomer && <TableCell>GST No.</TableCell>}
          <TableCell>Type</TableCell>
          <TableCell align='right'>Taxable</TableCell>
          <TableCell align='right'>CGST</TableCell>
          <TableCell align='right'>SGST</TableCell>
          <TableCell align='right'>IGST</TableCell>
          <TableCell align='right'>Cess/VAT</TableCell>
          <TableCell align='right'>Total</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(!rows || rows.length === 0) && (
          <TableRow>
            <TableCell colSpan={showCustomer ? 11 : 9} align='center'>
              No invoices in selected range.
            </TableCell>
          </TableRow>
        )}
        {(rows || []).map((r, i) => (
          <TableRow key={i} hover>
            <TableCell>{r.invoiceNumber}</TableCell>
            <TableCell>{fmtDate(r.invoiceDate)}</TableCell>
            {showCustomer && (
              <TableCell>
                {r.companyName || r.customerName || (r.tableNo ? `Table ${r.tableNo}` : '-')}
              </TableCell>
            )}
            {showCustomer && <TableCell>{r.customerGstNo || '-'}</TableCell>}
            <TableCell>
              <Chip size='small'
                label={r.invoiceType}
                color={r.invoiceType === 'B2B' ? 'info' : 'default'} />
            </TableCell>
            <TableCell align='right'>{fmtMoney(r.taxableValue)}</TableCell>
            <TableCell align='right'>{fmtMoney(r.cgst)}</TableCell>
            <TableCell align='right'>{fmtMoney(r.sgst)}</TableCell>
            <TableCell align='right'>{fmtMoney(r.igst)}</TableCell>
            <TableCell align='right'>{fmtMoney(r.cess)}</TableCell>
            <TableCell align='right'><strong>{fmtMoney(r.totalAmount)}</strong></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export default function GstReports() {
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate]     = useState(todayStr());
  const [hotelId, setHotelId]   = useState(0); // 0 = all hotels under company
  const [tab, setTab]           = useState(0);
  const [loading, setLoading]   = useState(false);
  const [data, setData]         = useState(null);
  const [snack, setSnack]       = useState({ open: false, msg: '', sev: 'success' });
  const notify = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const companyId = company?.id;

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const url = `${config.API_BASE_URL}/reports/gst`
        + `?companyId=${companyId}&hotelId=${hotelId}`
        + `&fromDate=${fromDate}&toDate=${toDate}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(await res.text());
      setData(await res.json());
    } catch (e) {
      console.error(e);
      notify('Failed to load GST report', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const b2b = useMemo(
    () => (data?.hotelInvoices || []).filter((r) => r.invoiceType === 'B2B'),
    [data],
  );
  const b2c = useMemo(
    () => [
      ...(data?.hotelInvoices || []).filter((r) => r.invoiceType === 'B2C'),
      ...(data?.restaurantInvoices || []),
    ],
    [data],
  );

  const exportExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();

    // Combined Sales sheet
    const allSales = [
      ...(data.hotelInvoices || []),
      ...(data.restaurantInvoices || []),
    ];
    const sales = [[
      'Invoice #', 'Date', 'Customer', 'GST No.', 'Type',
      'Taxable', 'CGST', 'SGST', 'IGST', 'Cess/VAT', 'Total',
    ]];
    allSales.forEach((r) =>
      sales.push([
        r.invoiceNumber, fmtDate(r.invoiceDate),
        r.companyName || r.customerName || (r.tableNo ? `Table ${r.tableNo}` : '-'),
        r.customerGstNo || '-', r.invoiceType,
        Number(r.taxableValue || 0), Number(r.cgst || 0),
        Number(r.sgst || 0), Number(r.igst || 0),
        Number(r.cess || 0), Number(r.totalAmount || 0),
      ]),
    );
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sales), 'Sales');

    // GSTR-1 B2B
    const b2bSheet = [[
      'Invoice #', 'Date', 'Customer GSTIN', 'Customer', 'Place of Supply',
      'Taxable', 'CGST', 'SGST', 'IGST', 'Total',
    ]];
    b2b.forEach((r) =>
      b2bSheet.push([
        r.invoiceNumber, fmtDate(r.invoiceDate), r.customerGstNo,
        r.companyName || r.customerName, r.placeOfSupply,
        Number(r.taxableValue || 0), Number(r.cgst || 0),
        Number(r.sgst || 0), Number(r.igst || 0), Number(r.totalAmount || 0),
      ]),
    );
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(b2bSheet), 'GSTR-1 B2B');

    // GSTR-1 B2C (consolidated by rate would be next-step; ship rows for now)
    const b2cSheet = [[
      'Invoice #', 'Date', 'Type', 'Taxable',
      'CGST', 'SGST', 'IGST', 'Cess/VAT', 'Total',
    ]];
    b2c.forEach((r) =>
      b2cSheet.push([
        r.invoiceNumber, fmtDate(r.invoiceDate),
        r.tableNo ? 'Restaurant' : 'Hotel',
        Number(r.taxableValue || 0), Number(r.cgst || 0),
        Number(r.sgst || 0), Number(r.igst || 0),
        Number(r.cess || 0), Number(r.totalAmount || 0),
      ]),
    );
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(b2cSheet), 'GSTR-1 B2C');

    // HSN-wise
    const hsn = [[
      'HSN', 'Tax Type', 'Rate %', 'Qty', 'Taxable',
      'CGST', 'SGST', 'VAT', 'Total',
    ]];
    (data.hsnSummary || []).forEach((r) =>
      hsn.push([
        r.hsnCode, r.taxType, Number(r.rate || 0),
        Number(r.totalQty || 0), Number(r.taxableValue || 0),
        Number(r.cgst || 0), Number(r.sgst || 0),
        Number(r.vat || 0), Number(r.totalAmount || 0),
      ]),
    );
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(hsn), 'HSN-wise');

    // Tax-slab-wise
    const slab = [['Rate %', 'Taxable', 'CGST', 'SGST', 'Total Tax', 'Total']];
    (data.taxSlabSummary || []).forEach((r) =>
      slab.push([
        Number(r.rate || 0), Number(r.taxableValue || 0),
        Number(r.cgst || 0), Number(r.sgst || 0),
        Number(r.totalTax || 0), Number(r.totalAmount || 0),
      ]),
    );
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(slab), 'Tax-Slab');

    // Daily totals
    const daily = [['Date', 'Taxable', 'Total Tax', 'Total']];
    (data.dailyTotals || []).forEach((r) =>
      daily.push([
        fmtDate(r.day), Number(r.taxableValue || 0),
        Number(r.totalTax || 0), Number(r.totalAmount || 0),
      ]),
    );
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(daily), 'Daily Totals');

    XLSX.writeFile(wb, `GST_Report_${fromDate}_to_${toDate}.xlsx`);
  };

  const exportCsvB2B = () => {
    if (!data) return;
    const rows = [
      ['GSTIN of Recipient', 'Receiver Name', 'Invoice Number', 'Invoice Date',
       'Invoice Value', 'Place of Supply', 'Reverse Charge', 'Invoice Type',
       'Rate', 'Taxable Value', 'CGST', 'SGST', 'IGST', 'Cess'],
    ];
    b2b.forEach((r) => {
      const rate = r.taxableValue > 0
        ? Math.round(((r.cgst + r.sgst + r.igst) / r.taxableValue) * 100)
        : 0;
      rows.push([
        r.customerGstNo, r.companyName || r.customerName,
        r.invoiceNumber, fmtDate(r.invoiceDate),
        Number(r.totalAmount || 0).toFixed(2),
        r.placeOfSupply, 'N', 'Regular', rate,
        Number(r.taxableValue || 0).toFixed(2),
        Number(r.cgst || 0).toFixed(2),
        Number(r.sgst || 0).toFixed(2),
        Number(r.igst || 0).toFixed(2),
        Number(r.cess || 0).toFixed(2),
      ]);
    });
    const csv = rows
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `GSTR1_B2B_${fromDate}_to_${toDate}.csv`;
    a.click();
  };

  return (
    <Container maxWidth='xl' sx={{ py: 3 }}>
      <Box className='no-print' display='flex' justifyContent='space-between' alignItems='center' mb={2} flexWrap='wrap' gap={1}>
        <Typography variant='h5' fontWeight='700'>GST Reports</Typography>
        <Box display='flex' gap={1} alignItems='center' flexWrap='wrap'>
          <TextField type='date' size='small' label='From' value={fromDate}
            onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField type='date' size='small' label='To' value={toDate}
            onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField select size='small' label='Hotel' value={hotelId}
            onChange={(e) => setHotelId(parseInt(e.target.value, 10))}
            sx={{ minWidth: 140 }}>
            <MenuItem value={0}>All Hotels</MenuItem>
            <MenuItem value={company?.hotelId || company?.id}>
              {company?.name || 'Current'}
            </MenuItem>
          </TextField>
          <Button variant='contained' startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Load
          </Button>
          <Button variant='outlined' startIcon={<PrintIcon />} onClick={() => window.print()} disabled={!data}>
            Print
          </Button>
          <Button variant='outlined' startIcon={<DownloadIcon />} onClick={exportExcel} disabled={!data}>
            Excel
          </Button>
        </Box>
      </Box>

      {loading && <Box display='flex' justifyContent='center' py={6}><CircularProgress /></Box>}

      {!loading && data && (
        <Box id='gst-report-print'>
          {/* KPI strip */}
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard title='Taxable Value' value={`${currency} ${fmtMoney(data.totalTaxableValue)}`} color='#1976d2' />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard title='Total CGST' value={`${currency} ${fmtMoney(data.totalCgst)}`} color='#2e7d32' />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard title='Total SGST' value={`${currency} ${fmtMoney(data.totalSgst)}`} color='#2e7d32' />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard title='Total IGST' value={`${currency} ${fmtMoney(data.totalIgst)}`} color='#0288d1' />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard title='Cess / VAT' value={`${currency} ${fmtMoney(data.totalCess)}`} color='#ed6c02' />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard title='Grand Total' value={`${currency} ${fmtMoney(data.totalAmount)}`}
                sub={`${data.b2bCount} B2B · ${data.b2cCount} B2C`} color='#9c27b0' />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant='scrollable' scrollButtons='auto'>
              <Tab label='Sales (All)' />
              <Tab label={`GSTR-1 B2B (${b2b.length})`} />
              <Tab label={`GSTR-1 B2C (${b2c.length})`} />
              <Tab label={`HSN-wise (${(data.hsnSummary || []).length})`} />
              <Tab label={`Tax Slabs (${(data.taxSlabSummary || []).length})`} />
              <Tab label='Daily Trend' />
            </Tabs>
          </Box>

          <Box sx={{ mt: 2 }}>
            {tab === 0 && (
              <Box>
                <Typography variant='subtitle1' fontWeight='700' color='primary' mb={1}>
                  Hotel Invoices
                </Typography>
                <SalesTable rows={data.hotelInvoices} />
                <Typography variant='subtitle1' fontWeight='700' color='primary' mt={3} mb={1}>
                  Restaurant Invoices
                </Typography>
                <SalesTable rows={data.restaurantInvoices} />
              </Box>
            )}

            {tab === 1 && (
              <Box>
                <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                  <Typography variant='subtitle1' fontWeight='700' color='primary'>
                    GSTR-1 B2B (Customers with GSTIN)
                  </Typography>
                  <Button size='small' variant='outlined' startIcon={<DownloadIcon />} onClick={exportCsvB2B}>
                    GSTR-1 CSV
                  </Button>
                </Box>
                <SalesTable rows={b2b} />
              </Box>
            )}

            {tab === 2 && (
              <Box>
                <Typography variant='subtitle1' fontWeight='700' color='primary' mb={1}>
                  GSTR-1 B2C (No customer GSTIN)
                </Typography>
                <SalesTable rows={b2c} showCustomer={false} />
              </Box>
            )}

            {tab === 3 && (
              <TableContainer component={Card}>
                <Table size='small'>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>HSN</TableCell>
                      <TableCell>Tax Type</TableCell>
                      <TableCell align='right'>Rate %</TableCell>
                      <TableCell align='right'>Qty</TableCell>
                      <TableCell align='right'>Taxable</TableCell>
                      <TableCell align='right'>CGST</TableCell>
                      <TableCell align='right'>SGST</TableCell>
                      <TableCell align='right'>VAT</TableCell>
                      <TableCell align='right'>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data.hsnSummary || []).length === 0 && (
                      <TableRow><TableCell colSpan={9} align='center'>No HSN data.</TableCell></TableRow>
                    )}
                    {(data.hsnSummary || []).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{r.hsnCode}</TableCell>
                        <TableCell>
                          <Chip size='small' label={r.taxType}
                            color={r.taxType === 'VAT' ? 'warning' : 'primary'} variant='outlined' />
                        </TableCell>
                        <TableCell align='right'>{Number(r.rate || 0).toFixed(0)}%</TableCell>
                        <TableCell align='right'>{Number(r.totalQty || 0)}</TableCell>
                        <TableCell align='right'>{fmtMoney(r.taxableValue)}</TableCell>
                        <TableCell align='right'>{fmtMoney(r.cgst)}</TableCell>
                        <TableCell align='right'>{fmtMoney(r.sgst)}</TableCell>
                        <TableCell align='right'>{fmtMoney(r.vat)}</TableCell>
                        <TableCell align='right'><strong>{fmtMoney(r.totalAmount)}</strong></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {tab === 4 && (
              <TableContainer component={Card}>
                <Table size='small'>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell align='right'>Rate %</TableCell>
                      <TableCell align='right'>Taxable</TableCell>
                      <TableCell align='right'>CGST</TableCell>
                      <TableCell align='right'>SGST</TableCell>
                      <TableCell align='right'>Total Tax</TableCell>
                      <TableCell align='right'>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data.taxSlabSummary || []).length === 0 && (
                      <TableRow><TableCell colSpan={6} align='center'>No slab data.</TableCell></TableRow>
                    )}
                    {(data.taxSlabSummary || []).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell align='right'><strong>{Number(r.rate || 0).toFixed(0)}%</strong></TableCell>
                        <TableCell align='right'>{fmtMoney(r.taxableValue)}</TableCell>
                        <TableCell align='right'>{fmtMoney(r.cgst)}</TableCell>
                        <TableCell align='right'>{fmtMoney(r.sgst)}</TableCell>
                        <TableCell align='right'>{fmtMoney(r.totalTax)}</TableCell>
                        <TableCell align='right'><strong>{fmtMoney(r.totalAmount)}</strong></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {tab === 5 && (
              <TableContainer component={Card}>
                <Table size='small'>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>Date</TableCell>
                      <TableCell align='right'>Taxable</TableCell>
                      <TableCell align='right'>Total Tax</TableCell>
                      <TableCell align='right'>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data.dailyTotals || []).length === 0 && (
                      <TableRow><TableCell colSpan={4} align='center'>No daily data.</TableCell></TableRow>
                    )}
                    {(data.dailyTotals || []).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{fmtDate(r.day)}</TableCell>
                        <TableCell align='right'>{fmtMoney(r.taxableValue)}</TableCell>
                        <TableCell align='right'>{fmtMoney(r.totalTax)}</TableCell>
                        <TableCell align='right'><strong>{fmtMoney(r.totalAmount)}</strong></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      )}

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Container>
  );
}
